import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import TeacherService from "../service/TeacherService";
import { normalizeRole, ROLE_TEACHER } from "../utils/authRole";

const TeacherProfileContext = createContext({
  teacherProfile: null,
  teacherProfileLoading: false,
  teacherProfileError: "",
});

export const TeacherProfileProvider = ({ userRole, tenantType = "", children }) => {
  const normalizedRole = normalizeRole(userRole);
  const isTeacherRole = normalizedRole === ROLE_TEACHER && String(tenantType).toUpperCase() !== "TUITION";
  const [teacherProfile, setTeacherProfile] = useState(() => TeacherService.getCachedProfile());
  const [teacherProfileLoading, setTeacherProfileLoading] = useState(false);
  const [teacherProfileError, setTeacherProfileError] = useState("");

  useEffect(() => {
    let isMounted = true;

    if (!isTeacherRole) {
      setTeacherProfile(null);
      setTeacherProfileLoading(false);
      setTeacherProfileError("");
      return undefined;
    }

    const cached = TeacherService.getCachedProfile();
    if (cached) {
      setTeacherProfile(cached);
    }

    setTeacherProfileLoading(!cached);
    setTeacherProfileError("");

    TeacherService.getProfile({ forceRefresh: !cached, preferCache: Boolean(cached) })
      .then((profile) => {
        if (!isMounted) return;
        setTeacherProfile(profile);
        setTeacherProfileError("");
      })
      .catch((error) => {
        if (!isMounted) return;
        setTeacherProfileError(error?.message || "Unable to fetch teacher profile.");
      })
      .finally(() => {
        if (isMounted) {
          setTeacherProfileLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isTeacherRole]);

  const value = useMemo(() => ({
    teacherProfile,
    teacherProfileLoading,
    teacherProfileError,
  }), [teacherProfile, teacherProfileLoading, teacherProfileError]);

  return (
    <TeacherProfileContext.Provider value={value}>
      {children}
    </TeacherProfileContext.Provider>
  );
};

export const useTeacherProfile = () => useContext(TeacherProfileContext);
